import {BookOpen, CalendarDays, ChevronRight, Ticket, Users} from 'lucide-react'
import React from 'react'
import {useIntl} from 'react-intl'

import {
  AttendanceInputSuggestion,
  AttendanceInputSuggestionIcon,
} from '../../modes/attendance/attendanceInputSuggestions'

type AttendanceInputSuggestionPillsProps = {
  suggestions: AttendanceInputSuggestion[]
  onSelect: (text: string) => void
  disabled?: boolean
  theme?: 'light' | 'dark'
  showLabel?: boolean
  embedded?: boolean
}

const iconFor = (icon: AttendanceInputSuggestionIcon | undefined, size: number) => {
  switch (icon) {
    case 'students':
      return <Users size={size} strokeWidth={2} />
    case 'subject':
      return <BookOpen size={size} strokeWidth={2} />
    case 'support':
      // Same headset glyph as the navbar's "Need Any Support?" button.
      return <i className='fa fa-headset' style={{fontSize: size - 1}} />
    case 'ticket':
      return <Ticket size={size} strokeWidth={2} />
    case 'calendar':
    default:
      return <CalendarDays size={size} strokeWidth={2} />
  }
}

const AttendanceInputSuggestionPills: React.FC<AttendanceInputSuggestionPillsProps> = ({
  suggestions,
  onSelect,
  disabled = false,
  showLabel = false,
  embedded = false,
}) => {
  const intl = useIntl()

  if (suggestions.length === 0) return null

  return (
    <div style={{marginBottom: showLabel && !embedded ? 10 : 0}}>
      {showLabel ? (
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--chatbot-text-muted)',
            marginBottom: 6,
          }}
        >
          {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.INPUT_SUGGESTIONS_LABEL'})}
        </div>
      ) : null}
      {embedded ? (
        <p className='chatbot-welcome-suggestions-intro'>
          {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.WELCOME_SUGGESTIONS_INTRO'})}
        </p>
      ) : null}
      <div
        role='list'
        aria-label={intl.formatMessage({id: 'CHATBOT.ATTENDANCE.INPUT_SUGGESTIONS_ARIA'})}
        className={`chatbot-suggestion-pills${embedded ? ' chatbot-suggestion-pills--in-bubble' : ''}`}
      >
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.id}
            type='button'
            role='listitem'
            disabled={disabled}
            onClick={() => (suggestion.onSelect ? suggestion.onSelect() : onSelect(suggestion.text))}
            className={`chatbot-suggestion-pill${embedded ? ' chatbot-suggestion-card' : ''}`}
          >
            <span
              className={`chatbot-suggestion-pill__icon chatbot-suggestion-pill__icon--${
                suggestion.icon || 'calendar'
              }`}
              aria-hidden='true'
            >
              {iconFor(suggestion.icon, embedded ? 16 : 15)}
            </span>
            {/* The card asks the full question; the compact pill only has room for the label. */}
            <span className='chatbot-suggestion-pill__text'>
              {embedded ? suggestion.text : suggestion.label}
            </span>
            {embedded ? (
              <ChevronRight
                className='chatbot-suggestion-card__chevron'
                size={16}
                aria-hidden='true'
              />
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )
}

export default AttendanceInputSuggestionPills
